import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { X, AlertTriangle, Loader2, Trash2, Eye, EyeOff } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import toast from 'react-hot-toast';

interface Props {
  onClose: () => void;
}

export default function ClearDataModal({ onClose }: Props) {
  useEscapeToClose(onClose);
  const [password, setPassword] = useState('');
  const [confirmationWord, setConfirmationWord] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const qc = useQueryClient();

  const clearDataMutation = useMutation({
    mutationFn: (pwd: string) => posApi.clearSalesData(pwd),
    onSuccess: () => {
      toast.success('All sales data cleared successfully!');
      qc.invalidateQueries();
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.response?.data?.exception || 'Failed to clear sales data. Please check your password.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmationWord !== 'DELETE') {
      setError('Please type DELETE to confirm.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    setError('');
    clearDataMutation.mutate(password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h3 className="font-semibold text-red-900 text-lg">Clear All Sales Data</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-red-400 hover:text-red-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          <p className="text-sm text-gray-600 mb-4">
            This action is <strong>irreversible</strong>. It will permanently delete:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-2">
            <li>All POS Invoices</li>
            <li>All POS Sales Returns</li>
            <li>All POS Shifts and Variances</li>
            <li>All Cash Ledgers</li>
            <li>This action will also reverse corresponding Stock entries, restoring your inventory levels.</li>
          </ul>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                required
                placeholder="DELETE"
                value={confirmationWord}
                onChange={(e) => setConfirmationWord(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter your password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full text-sm border rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={clearDataMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={clearDataMutation.isPending || confirmationWord !== 'DELETE' || !password}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {clearDataMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {clearDataMutation.isPending ? 'Clearing...' : 'Clear Sales Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
